import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardHeader, Nav, NavItem, NavLink, CardBody, Button, Col, Input, Row, Label, ModalBody, Form, TabPane, TabContent, Spinner, FormFeedback, FormGroup, Alert, CardFooter } from 'reactstrap';
import { DatePickerInput, DatesProvider } from '@mantine/dates';
import { Select } from '@mantine/core';
import { getEstablishmenQuery, getSectorList } from '../../../helpers/establishment/backend_helper';
import { getBeachCleaningReasonList } from '../../../helpers/residue/backend_helper';

const GeneralTab = ({
    dataBeachCleaning,
    validation,
    handleSector,
    handleReason
}) => {

    const prevEstablishmentIdRef = useRef(undefined);

    useEffect(() => {
        const prev = prevEstablishmentIdRef.current;
        prevEstablishmentIdRef.current = validation.values.establishment_id;

        // Solo resetear sector si el usuario cambió de un establecimiento a otro
        // (no durante la carga inicial de datos en modo edición)
        if (prev !== undefined && prev !== '' && prev !== validation.values.establishment_id) {
            handleSector(null);
            validation.setFieldValue('sector_id', '', false);
            validation.setFieldTouched('sector_id', false, false);
        }
    }, [validation.values.establishment_id]);

    // --- Establecimientos ---
    const [establishmentData, setEstablishmentData] = useState([]);
    const [establishmentLoading, setEstablishmentLoading] = useState(true);

    useEffect(() => {
        getEstablishmenQuery({
            fields: ['_id', 'name'],
            filters: { disable: { $eq: false } },
            pagination: { page: 1, pageSize: 999 }
        }).then(res => {
            const seen = new Set();
            const unique = res.data.filter(e => {
                if (seen.has(e._id)) return false;
                seen.add(e._id);
                return true;
            });
            setEstablishmentData(unique.map(e => ({ value: e._id, label: e.name })));
        }).finally(() => setEstablishmentLoading(false));
    }, []);

    // --- Sectores (depende de establishment_id) ---
    const [sectorItems, setSectorItems] = useState([]);
    const [sectorData, setSectorData] = useState([]);
    const [sectorLoading, setSectorLoading] = useState(false);

    useEffect(() => {
        if (!validation.values.establishment_id) {
            setSectorData([]);
            setSectorItems([]);
            return;
        }
        setSectorLoading(true);
        getSectorList().then(sectors => {
            const filtered = sectors.filter(sector => {
                const estId = sector.establishment_id
                    || sector.establishment?.id
                    || sector.establishment?._id
                    || (typeof sector.establishment === 'string' ? sector.establishment : '')
                    || '';
                return estId === validation.values.establishment_id;
            });
            const savedSector = dataBeachCleaning?.sector;
            let list = filtered;
            if (savedSector?.id) {
                const alreadyInList = filtered.some(s => (s.id || s._id) === savedSector.id);
                if (!alreadyInList) list = [savedSector, ...filtered];
            }
            const seen = new Set();
            const unique = list.filter(s => {
                const key = s.id || s._id;
                if (seen.has(key)) return false;
                seen.add(key);
                return true;
            });
            setSectorItems(unique);
            setSectorData(unique.map(s => ({ value: s.id || s._id, label: s.name })));
        }).finally(() => setSectorLoading(false));
    }, [validation.values.establishment_id]);

    // --- Motivos (depende de is_done) ---
    const [reasonItems, setReasonItems] = useState([]);
    const [reasonData, setReasonData] = useState([]);
    const [reasonLoading, setReasonLoading] = useState(false);

    useEffect(() => {
        if (typeof validation.values.is_done !== 'boolean') {
            setReasonData([]);
            setReasonItems([]);
            return;
        }
        setReasonLoading(true);
        getBeachCleaningReasonList({ is_done: validation.values.is_done }).then(reasons => {
            const seen = new Set();
            const unique = reasons.filter(r => {
                const key = r._id || r.id;
                if (seen.has(key)) return false;
                seen.add(key);
                return true;
            });
            setReasonItems(unique);
            setReasonData(unique.map(r => ({ value: r._id || r.id, label: r.name })));
        }).finally(() => setReasonLoading(false));
    }, [validation.values.is_done]);

    return (
        <React.Fragment>
            <div>
                <h5>Datos Generales</h5>
                <p className="text-muted">           
                </p>
                </div>
                
                <div>
                    <Row className="g-3">
                        <Col xxl={4} md={6}>
                            <Select
                                label={<>Establecimiento <span style={{ color: 'red' }}>*</span></>}
                                id='establishment_id'
                                placeholder={establishmentLoading ? 'Cargando...' : 'Seleccione Establecimiento'}
                                data={establishmentData}
                                value={validation.values.establishment_id || null}
                                onChange={(val) => {
                                    validation.setFieldValue('establishment_id', val || '', false);
                                    if (val) validation.setFieldTouched('establishment_id', true, false);
                                }}
                                disabled={establishmentLoading}
                                searchable
                                clearable
                                error={
                                    validation.touched['establishment_id'] && validation.errors['establishment_id']
                                        ? validation.errors['establishment_id'] : ''
                                }
                            />
                        </Col>

                        <Col xxl={4} md={6}>
                            <Select
                                label={<>Sector <span style={{ color: 'red' }}>*</span></>}
                                id='sector_id'
                                placeholder={sectorLoading ? 'Cargando...' : 'Seleccione Sector'}
                                data={sectorData}
                                value={validation.values.sector_id || null}
                                onChange={(val) => {
                                    validation.setFieldValue('sector_id', val || '', false);
                                    if (val) {
                                        validation.setFieldTouched('sector_id', true, false);
                                        const item = sectorItems.find(s => (s.id || s._id) === val);
                                        if (item) handleSector(item);
                                    } else {
                                        handleSector(null);
                                    }
                                }}
                                disabled={sectorLoading || !validation.values.establishment_id}
                                searchable
                                clearable
                                error={
                                    validation.touched['sector_id'] && validation.errors['sector_id']
                                        ? validation.errors['sector_id'] : ''
                                }
                            />
                        </Col>

                        <Col xxl={2} md={6}>
                            <DatesProvider settings={{ locale: 'es-mx', firstDayOfWeek: 1, weekendDays: [0], timezone: 'UTC' }}>
                                <DatePickerInput
                                    label={<>Fecha <span className='text-danger'>*</span></>}
                                    id='date'
                                    name='date'
                                    placeholder="Seleccione una fecha"
                                    value={validation.values.date || null}
                                    onChange={(date) => {
                                        validation.setFieldValue("date", date);
                                        if (date) {
                                            validation.setFieldTouched('date', true, false);
                                        }
                                    }}
                                    onBlur={() => {
                                        if (validation.values.date) {
                                            validation.setFieldTouched('date', true, false);
                                        }
                                    }}
                                    valueFormat='DD-MM-YYYY'
                                    maxDate={new Date()}
                                    error={
                                        validation.touched['date'] &&
                                            validation.errors['date']
                                            ? validation.errors['date'] : ''
                                    }
                                    clearable={true}
                                />
                            </DatesProvider>
                        </Col>
                        <Col xxl={2} md={6}>
                            <Select
                                label={<>¿Limpieza realizada? <span style={{ color: 'red' }}>*</span></>}
                                id='is_done'
                                placeholder='Seleccione'
                                data={[
                                    { value: 'true', label: 'Sí' },
                                    { value: 'false', label: 'No' },
                                ]}
                                value={
                                    validation.values.is_done === true ? 'true'
                                    : validation.values.is_done === false ? 'false'
                                    : null
                                }
                                onChange={(val) => {
                                    const prev = validation.values.is_done;
                                    const next = val === 'true' ? true : val === 'false' ? false : null;
                                    if (typeof prev === 'boolean' && next !== prev) {
                                        validation.setFieldValue('beach_cleaning_reason_id', '', false);
                                        validation.setFieldTouched('beach_cleaning_reason_id', false, false);
                                    }
                                    validation.setFieldValue('is_done', next, false);
                                }}
                                error={
                                    validation.touched['is_done'] && validation.errors['is_done']
                                        ? validation.errors['is_done'] : ''
                                }
                            />
                        </Col>
                        <Col xxl={4} md={6}>
                            <Select
                                label={<>{'Motivo ' + (validation.values.is_done === true ? 'realización' : 'no realización')} <span style={{ color: 'red' }}>*</span></>}
                                id='beach_cleaning_reason_id'
                                placeholder={reasonLoading ? 'Cargando...' : 'Seleccione Motivo'}
                                data={reasonData}
                                value={validation.values.beach_cleaning_reason_id || null}
                                onChange={(val) => {
                                    validation.setFieldValue('beach_cleaning_reason_id', val || '', false);
                                    if (val) {
                                        validation.setFieldTouched('beach_cleaning_reason_id', true, false);
                                        const item = reasonItems.find(r => (r._id || r.id) === val);
                                        if (item) handleReason(item);
                                    } else {
                                        handleReason(null);
                                    }
                                }}
                                disabled={reasonLoading || typeof validation.values.is_done !== 'boolean'}
                                searchable
                                clearable
                                error={
                                    validation.touched['beach_cleaning_reason_id'] && validation.errors['beach_cleaning_reason_id']
                                        ? validation.errors['beach_cleaning_reason_id'] : ''
                                }
                            />
                        </Col>
                        <Col xxl={2} md={6}>
                            <Label for='kilometers_traveled' className='form-label'>
                                Kilómetros recorridos <span className='text-danger'>*</span>
                            </Label>
                            <Input
                                name='kilometers_traveled'
                                id='kilometers_traveled'
                                className='form-control'
                                placeholder='Ingrese un número'
                                type='number'
                                validate={{ required: { value: true } }}
                                onChange={validation.handleChange}
                                onBlur={validation.handleBlur}
                                value={validation.values.kilometers_traveled}
                                invalid={
                                    validation.touched.kilometers_traveled &&
                                        validation.errors.kilometers_traveled
                                        ? true : false
                                }
                            />
                            {
                                validation.touched.kilometers_traveled &&
                                    validation.errors.kilometers_traveled ? (
                                    <FormFeedback type='invalid'>
                                        {validation.errors.kilometers_traveled}
                                    </FormFeedback>
                                ) : null
                            }
                        </Col>
                        <Col xxl={6} md={6}>
                            <Label for='observation' className='form-label'>
                                Observaciones
                            </Label>
                            <Input
                                name='observation'
                                id='observation'
                                className='form-control'
                                placeholder='Ingrese la observación'
                                type='textarea'
                                rows={4}
                                validate={{ required: { value: true } }}
                                onChange={validation.handleChange}
                                onBlur={validation.handleBlur}
                                value={validation.values.observation}
                                invalid={
                                    validation.touched.observation &&
                                        validation.errors.observation
                                        ? true : false
                                }
                            />
                            {
                                validation.touched.observation &&
                                    validation.errors.observation ? (
                                    <FormFeedback type='invalid'>
                                        {validation.errors.observation}
                                    </FormFeedback>
                                ) : null
                            }
                        </Col>                        
                    </Row>
                </div>

                {/* <div className="d-flex align-items-start gap-3 mt-4">
                <button
                    type="button"
                    className="btn btn-success btn-label right ms-auto nexttab nexttab"
                    onClick={() => {
                    toggleVerticalTab(activeVerticalTab + 1);
                    }}
                >
                    <i className="ri-arrow-right-line label-icon align-middle fs-16 ms-2"></i>
             
                </button>
                </div> */}
        </React.Fragment>
    )
}

export default GeneralTab;